import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import Logo from "@redotech/redo-web/logo.svg";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useRedoReferralsRpcClient } from "../rpc/client-context";
import { useAuthToken } from "./auth-token-context";
import { readImpersonationCodeFromUrl } from "./impersonation";
import { LoginHeroPanel } from "./login-hero-panel";
import * as loginCss from "./login.module.css";

type State =
  | { kind: "exchanging" }
  | { kind: "success" }
  | { kind: "error"; message: string };

const FALLBACK_ERROR =
  "This impersonation link could not be used. Ask the admin to re-issue one.";

export const ImpersonatePage = memo(function ImpersonatePage() {
  const location = useLocation();
  const code = readImpersonationCodeFromUrl(location.search);
  const rpcClient = useRedoReferralsRpcClient();
  const queryClient = useQueryClient();
  const { beginImpersonation } = useAuthToken();
  const navigate = useNavigate();
  const [state, setState] = useState<State>(() =>
    code === undefined
      ? { kind: "error", message: FALLBACK_ERROR }
      : { kind: "exchanging" },
  );

  // Single-use guard: React 18 StrictMode double-mounts effects and the
  // handoff code is consumable exactly once. Same pattern as VerifyPage.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!code || startedRef.current) {
      return;
    }
    startedRef.current = true;
    const controller = new AbortController();
    (async () => {
      try {
        const result = await rpcClient.impersonationExchange(
          { code },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) {
          return;
        }
        beginImpersonation(result.authToken);
        // Seed `getMe` so the dashboard renders without an extra RTT.
        queryClient.setQueryData(["me", result.authToken], result.referrer);
        // Strip the consumed code from the URL so reload / share-link
        // doesn't carry it. Replace not push — no back-button to a
        // spent code.
        history.replaceState(null, "", "/dashboard");
        // Preload dashboard chunk while we transition.
        void import("../dashboard");
        setState({ kind: "success" });
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        // 4xx messages are safe to show; 5xx responses are masked.
        setState({
          kind: "error",
          message: clientErrorMessage(err, FALLBACK_ERROR),
        });
      }
    })();

    return () => {
      controller.abort();
    };
    // Effect runs once per mount; code is read from URL and doesn't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={loginCss.container}>
      <div className={loginCss.leftPanel}>
        <div className={loginCss.logoContainer}>
          <a
            className={loginCss.logoLockup}
            href="https://www.redo.com"
            rel="noreferrer"
            target="_blank"
          >
            <Logo className={loginCss.logo} />
            <span className={loginCss.logoSubmark}>Referrals</span>
          </a>
        </div>
        <div className={loginCss.formWrapper}>
          <section className={loginCss.formArea}>
            {state.kind === "exchanging" && (
              <>
                <div className={loginCss.authIntro}>
                  <h1 className={loginCss.authTitle}>Opening dashboard</h1>
                  <p className={loginCss.authSubtitle}>
                    Validating your impersonation link…
                  </p>
                </div>
                <div className={loginCss.loadingContainer}>
                  <div className={loginCss.loadingSpinner} />
                </div>
              </>
            )}
            {state.kind === "success" && (
              <div className={loginCss.authIntro}>
                <h1 className={loginCss.authTitle}>Loading dashboard</h1>
                <p className={loginCss.authSubtitle}>Redirecting…</p>
              </div>
            )}
            {state.kind === "error" && (
              <>
                <div className={loginCss.formContainer}>
                  <div className={loginCss.authIntro}>
                    <h1 className={loginCss.authTitle}>
                      Couldn't open the dashboard
                    </h1>
                    <p className={loginCss.authSubtitle}>{state.message}</p>
                  </div>
                </div>
                <div className={loginCss.stackedActions}>
                  <RedoButton
                    buttonClassName={loginCss.primaryActionButton}
                    className={loginCss.fullWidthButton}
                    hierarchy="primary"
                    onClick={() => window.close()}
                    size="sm"
                    text="Close window"
                    type="button"
                  />
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <LoginHeroPanel />
    </div>
  );
});
